import React, { useEffect, useRef, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../components/layout/DashboardShell";
import { Card, Button, TextArea } from "../components/common/Primitives";
import { MessageSquare, Send, User as UserIcon } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { formatDateTime } from "../lib/datetime";
import { useAuth } from "../lib/AuthContext";

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [counterparty, setCounterparty] = useState(null);
  const [activeUserId, setActiveUserId] = useState(null);
  const [activeName, setActiveName] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const isEndUser = user?.role === "investor" || user?.role === "startup";

  const loadThreads = async () => {
    try {
      const [threadResp, cpResp] = await Promise.all([
        api.get("/messages/threads"),
        isEndUser ? api.get("/messages/counterparty") : Promise.resolve({ data: { counterparty: null } }),
      ]);
      const tlist = threadResp.data.threads || [];
      setThreads(tlist);
      const cp = cpResp.data.counterparty;
      setCounterparty(cp);
      // Auto-select the first thread OR the default counterparty for end users
      if (!activeUserId) {
        if (tlist.length) {
          setActiveUserId(tlist[0].counterparty.user_id);
          setActiveName(tlist[0].counterparty.name || tlist[0].counterparty.email);
        } else if (cp) {
          setActiveUserId(cp.user_id);
          setActiveName(cp.name || cp.email);
        }
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadThread = async (uid, displayName) => {
    setActiveUserId(uid);
    if (displayName) setActiveName(displayName);
    try {
      const { data } = await api.get(`/messages/thread/${uid}`);
      setMessages(data.messages || []);
      if (data.counterparty) setActiveName(data.counterparty.name || data.counterparty.email);
      // Refresh thread list so unread badges clear
      loadThreads();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const send = async () => {
    const body = draft.trim();
    if (!body || !activeUserId || sending) return;
    setSending(true);
    try {
      await api.post("/messages/send", { to_user_id: activeUserId, body });
      setDraft("");
      loadThread(activeUserId, activeName);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => {
    if (activeUserId) loadThread(activeUserId, activeName);
  }, [activeUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // SSE for real-time messages with graceful fallback to 25s poll
  useEffect(() => {
    if (!activeUserId) return;
    let closed = false;
    let sse = null;
    let pollInterval = null;

    const startPolling = () => {
      pollInterval = setInterval(() => {
        if (!closed) loadThread(activeUserId, activeName);
      }, 25000);
    };

    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      if (backend && typeof EventSource !== "undefined") {
        sse = new EventSource(`${backend}/api/messages/stream`, { withCredentials: true });
        sse.addEventListener("message", (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            // Reload if this is for the currently-open thread
            if (msg.from_user_id === activeUserId || msg.to_user_id === activeUserId) {
              loadThread(activeUserId, activeName);
            } else {
              // Refresh threads list to bump unread badges
              loadThreads();
            }
          } catch (_) { /* ignore malformed */ }
        });
        sse.addEventListener("notification", () => { loadThreads(); });
        sse.onerror = () => {
          // Fall back to polling on error
          if (!closed && !pollInterval) startPolling();
        };
      } else {
        startPolling();
      }
    } catch (_) {
      startPolling();
    }

    return () => {
      closed = true;
      if (sse) sse.close();
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [activeUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const showEmptyState = !activeUserId;

  return (
    <DashboardShell title="Messages" subtitle="Direct chat with your assigned VenturaZ contact">
      <Toaster richColors position="top-right" />
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6 min-h-[600px]" data-testid="messages-page">
        {/* Threads list */}
        <Card testId="messages-threads-list">
          <div className="overline mb-4 flex items-center gap-2"><MessageSquare size={14} /> Conversations</div>
          {isEndUser && counterparty && !threads.find((t) => t.counterparty.user_id === counterparty.user_id) && (
            <button
              onClick={() => loadThread(counterparty.user_id, counterparty.name || counterparty.email)}
              className={`w-full text-left border rounded-md p-3 mb-2 transition-colors ${activeUserId === counterparty.user_id ? "bg-gold/15 border-gold/50" : "border-navy/10 hover:border-navy/20"}`}
              data-testid="messages-default-counterparty"
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0"><UserIcon size={14} className="text-navy" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-sm truncate">{counterparty.name || counterparty.email}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Your {user?.role === "investor" ? "VenturaZ Manager" : "VenturaZ POC"}</div>
                </div>
              </div>
              <div className="text-xs text-ink-muted italic">Start a conversation…</div>
            </button>
          )}
          {threads.length === 0 && (!isEndUser || !counterparty) && (
            <div className="text-ink-muted text-sm" data-testid="messages-empty-threads">
              No conversations yet.{" "}
              {user?.role === "investor" || user?.role === "startup"
                ? "Your assigned VenturaZ contact will appear here once your account is approved."
                : "Assigned counterparties will appear here after you send or receive a first message."}
            </div>
          )}
          <div className="space-y-2">
            {threads.map((t) => (
              <button
                key={t.thread_key}
                onClick={() => loadThread(t.counterparty.user_id, t.counterparty.name || t.counterparty.email)}
                className={`w-full text-left border rounded-md p-3 transition-colors ${activeUserId === t.counterparty.user_id ? "bg-gold/15 border-gold/50" : "border-navy/10 hover:border-navy/20"}`}
                data-testid={`messages-thread-${t.counterparty.user_id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0"><UserIcon size={14} className="text-navy" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-heading text-sm truncate">{t.counterparty.name || t.counterparty.email}</div>
                      {t.unread_count > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-gold text-navy text-[10px] font-bold" data-testid={`messages-thread-unread-${t.counterparty.user_id}`}>{t.unread_count}</span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">{t.counterparty.role}</div>
                  </div>
                </div>
                <div className="text-xs text-ink-muted truncate">{t.last_message?.body}</div>
              </button>
            ))}
          </div>
        </Card>

        {/* Conversation panel */}
        <Card testId="messages-conversation">
          {showEmptyState ? (
            <div className="flex flex-col items-center justify-center h-full py-24 text-center">
              <MessageSquare size={48} className="text-ink-muted mb-3" />
              <div className="font-heading text-ink text-lg mb-1">Select a conversation</div>
              <div className="text-ink-muted text-sm max-w-md">Pick a thread on the left or start a new one with your assigned VenturaZ contact.</div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 pb-4 border-b border-navy/10 mb-4">
                <div className="w-10 h-10 rounded-full bg-navy/10 flex items-center justify-center"><UserIcon size={18} className="text-navy" /></div>
                <div>
                  <div className="font-heading text-ink" data-testid="messages-conversation-title">{activeName}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Direct chat</div>
                </div>
              </div>
              <div ref={scrollRef} className="h-[380px] overflow-y-auto space-y-3 mb-4 pr-1" data-testid="messages-list">
                {messages.length === 0 && (
                  <div className="text-ink-muted text-sm italic text-center py-16">No messages yet. Say hello — every message is logged for your VenturaZ Manager to action.</div>
                )}
                {messages.map((m) => {
                  const mine = m.from_user_id === user?.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`messages-item-${m.message_id}`}>
                      <div className={`max-w-[75%] rounded-lg px-3.5 py-2 text-sm ${mine ? "bg-navy text-white" : "bg-navy/[0.06] text-ink border border-navy/10"}`}>
                        <div className={`text-[10px] font-mono uppercase tracking-wider mb-1 ${mine ? "text-white/60" : "text-ink-muted"}`}>{m.from_email}</div>
                        <div className="whitespace-pre-wrap leading-relaxed">{m.body}</div>
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/50" : "text-ink-muted"}`}>{formatDateTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 items-end">
                <TextArea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type your message…"
                  testId="messages-draft"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      send();
                    }
                  }}
                />
                <Button onClick={send} disabled={sending || !draft.trim()} data-testid="messages-send-btn" variant="gold">
                  <Send size={14} className="inline mr-1" /> {sending ? "Sending…" : "Send"}
                </Button>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mt-2">⌘/Ctrl + Enter to send</div>
            </>
          )}
        </Card>
      </div>
    </DashboardShell>
  );
}
